import React from 'react'
// libs
import { useSelector } from 'react-redux'
import { formValueSelector } from 'redux-form'
// components
import TableTree from 'components/UI/TableTree'
import PlanItemRow from 'components/PageComponents/Plans/Edit/components/ObjectivesList/components/PlanItemRow'
import AddEntityForm from 'components/PageComponents/Plans/Edit/components/ObjectivesList/components/AddForm/AddEntityForm'
import PlanRootCreate from 'components/PageComponents/Plans/Edit/components/ObjectivesList/components/PlanRootCreate'
import PlanItemActions from 'components/PageComponents/Plans/Edit/components/ObjectivesList/components/PlanItemActions'
import PlanTableSettingsButton from 'components/PageComponents/Plans/Edit/components/ObjectivesList/components/PlanTableSettingsButton'
// constants
import { Forms } from 'constants/index'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// utils
import { get } from 'lodash'
import { getPlanColumnLabel } from 'constants/planColumns'
import { parseColumnSettings } from 'utils/planColumns'

// Plan-specific adapter around the generic TableTree: owns the row/actions/create-form
// renderers and the expand/move persistence, so ObjectivesList stays tree-agnostic while
// the legacy react-sortable-tree still lives alongside it.
const PlanTableTree = ({ treeData, isLoading, canDrag, canDrop, updatePlanTree }) => {
  const { fieldFilters, plan } = React.useContext(PlanEditPageContext)

  // Page search text + field filters (dim non-matching rows) — mirrors the legacy NodeContentRenderer.
  const searchText = useSelector((state) => formValueSelector(Forms.PAGE_SEARCH_FORM)(state, 'description'))

  // Data columns come from the plan's own settings (PlanColumnsSettingsModal); the description
  // stays the tree column and is always rendered first.
  const columns = React.useMemo(
    () =>
      parseColumnSettings(plan?.columnSettings).map(({ fieldId, width }) => ({
        key: fieldId,
        header: getPlanColumnLabel(fieldId).toUpperCase(),
        width
      })),
    [plan?.columnSettings]
  )

  const hasFilter = !!fieldFilters?.length
  const isSearchActive = !!searchText || hasFilter

  const isRowDimmed = (node) => {
    if (!isSearchActive) {
      return false
    }

    const matchesBackend = !hasFilter || node.matchesFilter !== false

    let matched
    if (searchText && node.title) {
      matched = matchesBackend && node.title.toLowerCase().includes(searchText.toLowerCase())
    } else if (hasFilter) {
      matched = matchesBackend
    } else {
      matched = false
    }

    return !matched
  }

  // Row click → open the entity's minimized modal (mirrors the legacy NodeContentRenderer handleClick).
  const handleRowClick = (node) => {
    if (document.getElementById('PopoverModal')) {
      return
    }

    node.handleClick?.({ parentPermissions: get(node, 'entity.parentPermissions') })()
  }

  // Flatten a nested tree (DFS) into the { id, expanded, parentId, treePosition } payload the backend expects.
  const flattenTreeForPersist = (nodes, getExpanded) => {
    const planItems = []
    let treePosition = 0

    const walk = (list, parentId) => {
      list.forEach((node) => {
        planItems.push({
          id: node.id,
          expanded: getExpanded ? getExpanded(node) : node.expanded,
          parentId: parentId || node.referencedParentId || null,
          treePosition
        })

        treePosition += 1

        if (node.children) {
          walk(node.children, node.id)
        }
      })
    }

    walk(nodes, null)

    return planItems.filter(({ id }) => typeof id === 'string')
  }

  // Persist expand/collapse to the backend (mirrors the legacy tree's synchronizeTree).
  const handleToggleExpand = (toggledNode, nextExpanded) => {
    updatePlanTree(
      flattenTreeForPersist(treeData, (node) => (node.id === toggledNode.id ? nextExpanded : node.expanded))
    )
  }

  // Persist a drag & drop reorder/reparent (positions + parentId).
  const handleMoveNode = (nextTree) => {
    updatePlanTree(flattenTreeForPersist(nextTree))
  }

  // Render inline create-forms for helper nodes in the table (objective/action/kpi).
  // Root-level creation lives below the table (see <PlanRootCreate />), so the create-root node renders nothing.
  const renderCreateForm = (node) => {
    if (node.isCreateRoot) {
      return null
    }

    if (node.isCreateObjective) {
      return (
        <AddEntityForm
          level={node.newEntityLevel}
          form={`CREATE_OBJECTIVE_${node.id}`}
          label={`Enter a description for this ${get(node, 'newEntityLevel.description', 'item')}`}
          onSubmit={node.handleTableSubmit}
          onClose={node.handleTableClose}
        />
      )
    }

    if (node.isCreateAction) {
      return (
        <AddEntityForm
          level={node.newEntityLevel}
          form={`CREATE_ACTION_${node.id}`}
          label={'Enter a description for this action'}
          onSubmit={node.handleTableSubmit}
          onClose={node.handleTableClose}
        />
      )
    }

    if (node.isCreateKPI) {
      return (
        <AddEntityForm
          level={node.newEntityLevel}
          form={`CREATE_KPI_${node.id}`}
          label={'Enter a description for this indicator'}
          onSubmit={node.handleTableSubmit}
          onClose={node.handleTableClose}
        />
      )
    }

    return null
  }

  return (
    <TableTree
      columns={columns}
      treeData={treeData}
      isLoading={isLoading}
      isRowDimmed={isRowDimmed}
      rowComponent={PlanItemRow}
      footer={<PlanRootCreate />}
      onRowClick={handleRowClick}
      onMoveNode={handleMoveNode}
      forceExpandAll={isSearchActive}
      renderCreateForm={renderCreateForm}
      onToggleExpand={handleToggleExpand}
      canDrag={(node) => canDrag({ node })}
      actionsHeader={<PlanTableSettingsButton />}
      canDrop={(node, nextParent) => canDrop({ node, nextParent })}
      renderRowActions={(node) => <PlanItemActions node={node} />}
    />
  )
}

export default PlanTableTree
